import { Box, Button, Center, HStack, IconButton, Text, VStack } from '@chakra-ui/react';
import { Upload } from 'lucide-react';
import { ReactNode, useCallback, useContext } from 'react';
import { DropEvent, FileRejection, useDropzone } from 'react-dropzone';

import { FileUploadContext } from './FileUploadContext';

/**
 * FileUploadDropzone component
 *
 * This component creates a dropzone area for drag-and-drop file uploads.
 * It also allows for custom content to be displayed within the dropzone.
 * It must be used within a FileUploadContextProvider component.
 *
 * @component
 * @example
 * <FileUploadDropzone>
 *   <Text>Drag files here or click to select</Text>
 * </FileUploadDropzone>
 */
export function FileUploadDropzone({ children }: { children?: ReactNode }) {
  const context = useContext(FileUploadContext);

  if (!context) {
    throw new Error('FileUploadDropzone must be used within a FileUploadContextProvider');
  }

  const {
    accept,
    allowMultipleFiles,
    maxFiles,
    handleFileDropzoneChange,
    minSizePerFileKB,
    maxSizePerFileMB,
    defaultAttachedToId,
    defaultAttachedToKind,
  } = context;

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[], event: DropEvent) => {
      handleFileDropzoneChange(
        acceptedFiles,
        fileRejections,
        event,
        defaultAttachedToKind,
        defaultAttachedToId,
      );
    },
    [handleFileDropzoneChange, defaultAttachedToKind, defaultAttachedToId],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    multiple: allowMultipleFiles,
    maxFiles,
    minSize: minSizePerFileKB * 1024, // in bytes
    maxSize: maxSizePerFileMB * 1024 * 1024, // in bytes
  });

  return (
    <Center
      {...getRootProps()}
      borderWidth="2px"
      borderRadius="lg"
      borderStyle="dotted"
      px="6"
      py="4"
      height="100%"
      width="100%"
      minHeight="200px"
      borderColor={isDragActive ? 'border.accent' : 'brand.300'}
      transition="all 0.2s"
      _hover={{ borderColor: 'border.accent' }}
    >
      <input {...getInputProps()} />
      <VStack spacing="3">
        {isDragActive ? (
          <Text textStyle="sm" color="fg.muted">
            Drop the files here...
          </Text>
        ) : (
          <Box width="100%" height="100%">
            {children != null ? (
              children
            ) : (
              <VStack spacing="3">
                <IconButton
                  aria-label={'Upload'}
                  icon={<Upload size="24" color="black" />}
                  variant="ghost"
                />
                <VStack spacing="1">
                  <HStack spacing="1" whiteSpace="nowrap">
                    <Button variant="text" color="fg.muted" size="md">
                      Click to upload
                    </Button>
                    <Text textStyle="sm" color="fg.muted">
                      or drag and drop
                    </Text>
                    <Text textStyle="sm" color="fg.muted">
                      (up to {maxFiles} files, {maxSizePerFileMB}MB each)
                    </Text>
                  </HStack>
                </VStack>
              </VStack>
            )}
          </Box>
        )}
      </VStack>
    </Center>
  );
}
